/**
 * SupplierDocumentsPanel — compact scrollable history of the supplier's other
 * DIN/EXP/BIL records, rendered next to the memo field in DocumentModal.
 *
 * Purpose: give the reviewer immediate visibility of what else exists for the
 * selected supplier — especially bills already captured directly in the
 * external accounting system (imported as BIL/EXP) that a new DIN would
 * duplicate on publish. Rows flagged by the supplier duplicate check are
 * highlighted amber; clicking a row opens that document (editable documents
 * open in DocumentModal, read-only in DocumentViewModal — handled by the
 * parent's onOpen callback).
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, FileText, Loader2 } from 'lucide-react';
import { authService } from '@/lib/authService';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import type { Document } from '@/hooks/useDocumentsWorkItems';

const SUPPLIER_DOC_TYPES: Record<string, string> = {
  din: 'DIN',
  expense: 'EXP',
  supplier_invoice: 'BIL',
};

export interface SupplierDocumentsPanelProps {
  /** Supplier contact id — the list is scoped to this supplier. */
  contactId: number;
  /** The currently open document — excluded from the list. */
  excludeDocumentId?: string | number | null;
  /** Work item ids flagged by the duplicate check — highlighted amber. */
  duplicateIds?: ReadonlySet<string>;
  /** Called when a row is clicked; the parent fetches and opens the document. */
  onOpen: (workItemId: string) => void;
  isDarkMode: boolean;
  formatAmount: (amount: number) => string;
  className?: string;
}

export default function SupplierDocumentsPanel({
  contactId,
  excludeDocumentId,
  duplicateIds,
  onOpen,
  isDarkMode,
  formatAmount,
  className,
}: SupplierDocumentsPanelProps) {
  const [documents, setDocuments] = useState<Document[] | null>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const token = authService.getTokens()?.access_token;
      if (!token) return;
      const params = new URLSearchParams({
        doc_type: 'din,expense,supplier_invoice',
        contact_id: String(contactId),
        // Include DINs whose supplier link still lives in custom_fields
        // (AI-matched / technician-declared, awaiting confirmation).
        contact_linkage: 'true',
        limit: '50',
        sort_by: 'created_at',
        sort_order: 'desc',
      });
      fetch(`/api/v1/work-items/documents?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((json: { data?: Document[] } | null) => {
          if (requestSeq.current !== seq) return;
          const rows = Array.isArray(json?.data) ? json!.data : [];
          const exclude = excludeDocumentId != null ? String(excludeDocumentId) : null;
          setDocuments(exclude ? rows.filter((d) => String(d.id) !== exclude) : rows);
        })
        .catch((err: unknown) => {
          if (requestSeq.current !== seq || isSilentFetchError(err)) return;
          setDocuments([]);
        });
    }, 200);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [contactId, excludeDocumentId]);

  const label = (
    <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400">
      <FileText className="w-3.5 h-3.5" />
      Supplier history
      {documents != null && <span className="text-gray-400 dark:text-gray-500">({documents.length})</span>}
    </span>
  );

  const formatRowDate = (doc: Document): string => {
    const raw = doc.transaction_date || doc.issued_at || doc.created_at;
    if (!raw) return '';
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  };

  return (
    <div className={`min-w-0 ${className ?? ''}`}>
      <div className="mb-1">{label}</div>
      <div
        className={`max-h-[4.5rem] overflow-y-auto rounded border divide-y text-xs ${
          isDarkMode ? 'border-gray-700 divide-gray-700/60 bg-gray-800/50' : 'border-gray-300 divide-gray-200 bg-white'
        }`}
      >
        {documents === null ? (
          <div className="flex items-center justify-center gap-1.5 py-3 text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading…
          </div>
        ) : documents.length === 0 ? (
          <div className="py-3 text-center text-gray-400 dark:text-gray-500">
            No other records for this supplier
          </div>
        ) : (
          documents.map((doc) => {
            const docType = String(doc.doc_type || (doc as { item_type?: string }).item_type || '');
            const isDuplicate = duplicateIds?.has(String(doc.id)) === true;
            return (
              <button
                key={String(doc.id)}
                type="button"
                onClick={() => onOpen(String(doc.id))}
                className={`w-full flex items-center gap-1.5 px-2 py-1.5 text-left transition-colors ${
                  isDuplicate
                    ? isDarkMode ? 'bg-amber-900/30 hover:bg-amber-900/50' : 'bg-amber-50 hover:bg-amber-100'
                    : isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                }`}
                title={isDuplicate ? 'Flagged as a possible duplicate of this document' : `Open ${doc.doc_number || doc.item_number || doc.id}`}
              >
                <span
                  className={`flex-shrink-0 px-1 rounded text-[10px] font-semibold ${
                    docType === 'supplier_invoice'
                      ? isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                      : docType === 'expense'
                        ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {SUPPLIER_DOC_TYPES[docType] || docType.toUpperCase() || 'DOC'}
                </span>
                <span className="flex-1 min-w-0 truncate font-medium text-gray-700 dark:text-gray-300">
                  {doc.doc_number || doc.item_number || `#${doc.id}`}
                </span>
                <span className="flex-shrink-0 text-gray-500 dark:text-gray-400">
                  {formatRowDate(doc)}
                </span>
                <span className="flex-shrink-0 font-medium text-gray-700 dark:text-gray-300">
                  {formatAmount(Number(doc.grand_total) || 0)}
                </span>
                {isDuplicate && (
                  <AlertTriangle className="w-3 h-3 flex-shrink-0 text-amber-500" />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
